import { api } from "./client";

export interface GraphDefinition {
  nodes: Array<{
    id: string;
    type: string;
    position: { x: number; y: number };
    data: Record<string, unknown>;
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    label?: string;
    condition?: string;
  }>;
  config: Record<string, unknown>;
}

export interface AgentGraph {
  id: string;
  companyId: string;
  name: string;
  description: string | null;
  graphDefinition: GraphDefinition;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface GraphRun {
  id: string;
  companyId: string;
  graphId: string;
  threadId: string | null;
  status: string;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  events: Array<Record<string, unknown>>;
  totalTokens: number;
  costCents: number;
  startedAt: string;
  finishedAt: string | null;
}

export const graphsApi = {
  list: (companyId: string, status?: string) => {
    const qs = status ? `?status=${status}` : "";
    return api.get<AgentGraph[]>(`/companies/${companyId}/graphs${qs}`);
  },

  get: (id: string) => api.get<AgentGraph>(`/graphs/${id}`),

  create: (companyId: string, data: { name: string; description?: string; graphDefinition: GraphDefinition }) =>
    api.post<AgentGraph>(`/companies/${companyId}/graphs`, data),

  update: (id: string, data: Partial<{ name: string; description: string | null; graphDefinition: GraphDefinition; status: string }>) =>
    api.patch<AgentGraph>(`/graphs/${id}`, data),

  remove: (id: string) => api.delete<AgentGraph>(`/graphs/${id}`),

  // Runs (queue-driven orchestrator; company-scoped endpoints)
  /** Start a run of the graph. Returns the runId (poll getRun for status). */
  run: (companyId: string, graphId: string, input?: { messages?: Array<{ role: "user" | "assistant"; content: string }>; state?: Record<string, unknown> }) =>
    api.post<{ runId: string }>(`/companies/${companyId}/graphs/${graphId}/run`, input ?? {}),

  listRuns: (companyId: string, graphId: string, limit = 50) =>
    api.get<GraphRun[]>(`/companies/${companyId}/graphs/${graphId}/runs?limit=${limit}`),

  getRun: (companyId: string, graphId: string, runId: string) =>
    api.get<GraphRun & { items?: Array<{ id: string; nodeId?: string; status: string; error: string | null; result: Record<string, unknown> | null }> }>(
      `/companies/${companyId}/graphs/${graphId}/runs/${runId}`,
    ),

  // Deploy / Undeploy
  deploy: (graphId: string, data?: { agentName?: string; agentId?: string }) =>
    api.post<{ agentId: string; graphId: string; status: string; heartbeatConfig?: { wakeupTrigger: string; cronExpression?: string } }>(`/graphs/${graphId}/deploy`, data ?? {}),

  undeploy: (graphId: string) =>
    api.post<{ success: boolean }>(`/graphs/${graphId}/undeploy`, {}),

  validate: (graphId: string) =>
    api.post<{ valid: boolean; errors: Array<{ field: string; message: string }> }>(`/graphs/${graphId}/validate`, {}),

  /**
   * Record a HITL decision on a parked graph interrupt (queue-driven
   * orchestrator). `approved` advances the run; `rejected` fail-closes it.
   */
  resumeWorkItem: (
    companyId: string,
    graphId: string,
    runId: string,
    itemId: string,
    decision: "approved" | "rejected",
    note?: string,
  ) =>
    api.post<{ ok: true }>(
      `/companies/${companyId}/graphs/${graphId}/runs/${runId}/items/${itemId}/resume`,
      { decision, note },
    ),
};
